import React, { useEffect, useMemo, useState } from "react";
import { Provider, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import { FlagsProvider } from "flagged";

import BannerNotification from "@cayuse/shared-react.components.banner-notification"
import CayuseCommonHeader from "@cayuse/shared-react.components.cayuse-common-header";
import { setUseFontAwesomeOverrides } from "@cayuse/shared-react.components.icon";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import { AuthProvider, useAuth } from "@cayuse/shared-react.utils.auth";
import { registerGetTokenSilently } from "@cayuse/shared-react.utils.http";
import { registerGetTokenSilently as cayuseRegisterGetTokenSilently } from "@cayuse/shared-react.utils.http";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { TenantProvider } from "@cayuse/shared-react.context.tenant";

import NavBar from "components/NavBar";
import Pendo from "components/Pendo";
import Routing from "components/Routing";
import UpdateCheck from './components/UpdateCheck';
import {
	FORM_MANAGERS,
	IS_DEMO,
	IS_ENG,
	IS_FT,
	IS_PROD,
	IS_RGRS,
	IS_UAT,
	LOGOUT_REDIRECT_ROUTE,
	LOGOUT_ROUTE,
	PRODUCT_KEY,
} from "globals/constants";
import { useAuthenticationRequired } from "hooks";
import { setDictionary } from "store/actions/dictionary";
import { fetchShell, setCayuseHeader } from "store/actions/shell";
import { getBrowserLanguage, setHTMLElementLangAttribute } from "utils";
import history from "utils/history";
import { getTokenSilently } from "utils/proxy";
import store from "store/store";
import { shellSelectors, tenantSelectors } from "store/selectors";

import "@cayuse/shared-react.globals.css/dist/variables.colors.css";

import styles from "./styles.css";

require("moment/min/locales.min");
require("@fortawesome/fontawesome-free/js/all.min");

setHTMLElementLangAttribute(getBrowserLanguage());
setUseFontAwesomeOverrides(true);
registerGetTokenSilently(getTokenSilently);
cayuseRegisterGetTokenSilently(getTokenSilently);

const refreshInterval = process.env.REACT_APP_PAGE_REFRESH_INTERVAL;

const onRedirectCallback = appState => {
	history.push(
		appState && appState.targetUrl
			? appState.targetUrl
			: window.location.pathname
	);
};

const Auth0App = () => {
	const [ didFetchShell, setDidFetchShell ] = useState(false);
	const { loading: isAuthenticating } = useAuthenticationRequired();
	const { user } = useAuth();

	const isFormManager = useMemo(() => {
		// eslint-disable-next-line camelcase
		const username = user?.preferred_username;

		return username ? FORM_MANAGERS.includes(username.toLowerCase()) : false;
	}, [ user ]);

	const {
		authenticationToken,
		customerImage,
		product,
		userId,
	} = useSelector(shellSelectors.cayuseHeader);
	const tenantId = useSelector(tenantSelectors.tenantId);

	useEffect(() => {
		if (!isAuthenticating && !didFetchShell && isNonEmptyString(authenticationToken)) {
			fetchShell()
				.then(() => {
					setDidFetchShell(true);
				})
				.catch(() => {
					setDidFetchShell(false);
				});
		}
	}, [ isAuthenticating, didFetchShell, authenticationToken ]);

	useEffect(() => {
		if (!!userId && !!window.playerzero) {
			// PlayerZero has loaded
			window.playerzero.identify(userId, {
				name: user?.preferred_username,
				group: user?.tenantId
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ userId, window.playerzero ]);

	return !isAuthenticating && didFetchShell
		? (
			<>
				<a className="accessibility-text skip-link" href="#content-main">Skip to Main Content</a>
				<div className="App">
					<Pendo />
					<CayuseCommonHeader
						authenticationToken={authenticationToken || ""}
						customerImage={customerImage}
						logoutRoute={LOGOUT_ROUTE}
						logoutRedirectTo={LOGOUT_REDIRECT_ROUTE}
						product={product}
						userId={userId || ""}
						scriptBranch="v4"
					/>
					<div aria-live="polite">
						<BannerNotification role="presentation" tenantId={tenantId} language={getBrowserLanguage()} />
					</div>
					<Routing isFormManager={isFormManager}>
						<NavBar isFormManager={isFormManager} />
						<ReactTooltip className={styles.globalTooltip} effect="solid" />
						<ReactTooltip className={styles.globalTooltip} effect="solid" id="react-tooltip" multiline={true} />
					</Routing>
					<UpdateCheck interval={refreshInterval} />
				</div>
			</>
		)
		: <LoadingIndicator />;
};

export const FEATURES = {
	monument: true,
	netarts: IS_FT || IS_ENG || IS_RGRS || IS_DEMO || IS_UAT || IS_PROD,
};

const App = () => {
	setDictionary(getBrowserLanguage());

	return (
		<FlagsProvider features={FEATURES}>
			<Provider store={store}>
				<AuthProvider
					redirect_uri={`${window.location.origin}/`}
					onRedirectCallback={onRedirectCallback}
					setCayuseHeader={setCayuseHeader}
					PRODUCT_KEY={PRODUCT_KEY}
				>
					<TenantProvider>
						<Auth0App/>
					</TenantProvider>
				</AuthProvider>
			</Provider>
		</FlagsProvider>
	);
};

export default App;
